import { IconButton } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import {
	RecognizedLabelsStructureType,
	TableCellCandidateType,
	TableRecognizedLabel,
} from 'entities/Files/types';
import { ColumnPreference } from 'entities/Preferences/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import DeleteTableModal from 'components/file-editor-components/delete-table-modal';
import Collapse from 'components/collapse';
import { CellTarget } from 'components/file-editor-components/table-fields/types';
import SingleTable from 'components/file-editor-components/table-fields/SingleTable';

type TableFieldsProps = {
	labels: RecognizedLabelsStructureType[];
	selectedSourceId?: string | null;
	onSelectSource?: (sourceId: string | null) => void;
	drawMode?: boolean;
	pendingCellTarget?: CellTarget | null;
	onCellTargetClick?: (target: CellTarget) => void;
	onCellContentChange?: (target: CellTarget, content: string) => void;
	onCellCandidateSelect?: (target: CellTarget, candidate: TableCellCandidateType) => void;
	onCellDelete?: (target: CellTarget) => void;
	onDeleteTable?: (tableIndex: number) => void;
	onAddRow?: (tableIndex: number) => void;
	onDeleteRow?: (tableIndex: number, rowIndex: number) => void;
	onDeleteColumn?: (tableIndex: number, columnIndex: number) => void;
	onReorderColumns?: (tableIndex: number, columnOrder: number[]) => void;
	getColumnPinPreferences?: (tableLabel: string | undefined) => ColumnPreference[];
	onTogglePinColumn?: (tableIndex: number, columnIndex: number) => void;
};

const TableFields: FC<TableFieldsProps> = ({
	labels,
	selectedSourceId,
	onSelectSource,
	drawMode,
	pendingCellTarget,
	onCellTargetClick,
	onCellContentChange,
	onCellCandidateSelect,
	onCellDelete,
	onDeleteTable,
	onAddRow,
	onDeleteRow,
	onDeleteColumn,
	onReorderColumns,
	getColumnPinPreferences,
	onTogglePinColumn,
}) => {
	const [pendingDeleteTableIndex, setPendingDeleteTableIndex] = useState<number | null>(null);

	const tables = useMemo(
		() =>
			labels
				.map((label, labelIndex) => ({ label, tableIndex: labelIndex }))
				.filter(
					(entry): entry is { label: TableRecognizedLabel; tableIndex: number } =>
						entry.label.type === 'table',
				),
		[labels],
	);

	const handleConfirmDelete = () => {
		if (pendingDeleteTableIndex !== null) onDeleteTable?.(pendingDeleteTableIndex);
		setPendingDeleteTableIndex(null);
	};

	return (
		<>
			{tables.map(({ label: table, tableIndex }) => (
				<Collapse
					key={tableIndex}
					expandSignal={
						selectedSourceId?.startsWith(`cell-${tableIndex}-`) ? selectedSourceId : undefined
					}
					label={table?.label}
					headerExtra={
						onDeleteTable && (
							<IconButton
								onClick={() => setPendingDeleteTableIndex(tableIndex)}
								size={'small'}
								sx={{ padding: '4px' }}
							>
								<Icon
									id={'close_round'}
									color={ColorPalette.violetRed}
									style={{ width: '18px', height: '18px' }}
								/>
							</IconButton>
						)
					}
				>
					<SingleTable
						table={table}
						tableIndex={tableIndex}
						selectedSourceId={selectedSourceId}
						onSelectSource={onSelectSource}
						drawMode={drawMode}
						pendingCellTarget={pendingCellTarget}
						onCellTargetClick={onCellTargetClick}
						onCellContentChange={onCellContentChange}
						onCellCandidateSelect={onCellCandidateSelect}
						onCellDelete={onCellDelete}
						onAddRow={onAddRow}
						onDeleteRow={onDeleteRow}
						onDeleteColumn={onDeleteColumn}
						onReorderColumns={onReorderColumns}
						getColumnPinPreferences={getColumnPinPreferences}
						onTogglePinColumn={onTogglePinColumn}
					/>
				</Collapse>
			))}
			<DeleteTableModal
				open={pendingDeleteTableIndex !== null}
				onCancel={() => setPendingDeleteTableIndex(null)}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};

export default TableFields;
